import React, { useState, useEffect } from "react";
import { CalendarMinus2, ClipboardList, Plus, CalendarClock, CheckCircle, Settings } from "lucide-react";
import MyLeave from "../../components/admin/tabs/leave/myLeave";
import LeaveRequests from "../../components/admin/tabs/leave/leaveRequests";
import AddRequest from "../../components/admin/tabs/leave/addRequest";
import AddPublicHoliday from "../../components/admin/tabs/leave/publicHoliday";
import AddAnnualLeave from "../../components/admin/tabs/leave/annualLeave";
import SettingLeave from "../../components/admin/tabs/leave/settingLeave";
import { getEmployeeData } from "../../utils/localStorage";
import { getRequest } from "../../service";

const Leave = () => {
  const userData = getEmployeeData();
  const [activeTab, setActiveTab] = useState("myLeave");

  // Tab definitions
  const allTabs = [
    { id: "myLeave", icon: <CalendarMinus2 size={20} />, component: <MyLeave /> },
    { id: "addRequest", icon: <Plus size={20} />, component: <AddRequest /> },
    { id: "LeaveRequests", icon: <ClipboardList size={20} />, component: <LeaveRequests /> },
    { id: "PublicHoliday", icon: <CalendarClock size={20} />, component: <AddPublicHoliday /> },
    { id: "AddLeaves", icon: <CalendarMinus2 size={20} />, component: <AddAnnualLeave /> },
    // { id: "Setting", icon: <Settings size={20} />, component: <SettingLeave /> }
  ];

  // Map tab id to Leaves permission key
  const leaveTabPermissionMap = {
    LeaveRequests: "Leaves Request",
    PublicHoliday: "Public Holiday",
    AddLeaves: "Add Leaves",
    Setting: "Setting", // Not in permissions, handled below
  };

  // Filter tabs based on role and permissions
  let filteredTabs = [];
  const role = userData?.role;
  const leavesPerms = userData?.designation?.permissions?.Leaves || {};

  if (role === "companyadmin") {
    // Show all except My Leave and Add Request
    filteredTabs = allTabs.filter(tab => tab.id !== "myLeave" && tab.id !== "addRequest");
  } else {
    // Always show My Leave and Add Request
    filteredTabs = allTabs.filter(tab => {
      if (tab.id === "myLeave" || tab.id === "addRequest") return true;
      if (tab.id === "Setting") {
        // Show Setting if any Leaves permission is not empty
        return Object.values(leavesPerms).some(arr => Array.isArray(arr) && arr.length > 0);
      }
      const permKey = leaveTabPermissionMap[tab.id];
      if (!permKey) return true; // If not mapped, show
      const permArr = leavesPerms[permKey];
      // Show if permission array is not empty or contains 'view'
      return Array.isArray(permArr) && (permArr.includes("view") || permArr.length > 0);
    });
  }

  // Fetch Leave stats (company-level)
  const [stats, setStats] = useState({
    totalAnnualLeave: 0,
    totalLeaveRequests: 0,
    publicHolidays: 0,
    approved: 0,
    rejected: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const companyId = userData?.userId;
        if (!companyId) return;
        setLoading(true);
        const currentYear = new Date().getFullYear();
        const response = await getRequest(`/api/leaveRequest/stats?companyId=${companyId}&year=${currentYear}`);
        if (response) {
          const approved = response?.leaveRequestStatus?.approved || 0;
          const rejected = response?.leaveRequestStatus?.rejected || 0;
          setStats({
            totalAnnualLeave: response.totalAnnualLeave || 0,
            totalLeaveRequests: response.totalLeaveRequests || 0,
            publicHolidays: response.publicHolidays || 0,
            approved,
            rejected,
          });
        }
      } catch (error) {
        console.error("Error fetching leave stats:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.userId]);

  // If the activeTab is not in filteredTabs, reset to first available
  React.useEffect(() => {
    if (!filteredTabs.some(tab => tab.id === activeTab)) {
      setActiveTab(filteredTabs[0]?.id || "");
    }
    // eslint-disable-next-line
  }, [filteredTabs.map(tab => tab.id).join(",")]);

  const handleTabClick = (tab) => {
    setActiveTab(tab);
  };

  const activeComponent = filteredTabs.find(tab => tab.id === activeTab)?.component;
  const tiles = [
    {
      title: "Total Annual Leave",
      value: String(stats.totalAnnualLeave || 0),
      icon: <CalendarMinus2 className="h-6 w-6 text-blue-500" />,
      color: "bg-blue-50"
    },
    {
      title: "Request",
      value: String(stats.totalLeaveRequests || 0),
      icon: <ClipboardList className="h-6 w-6 text-orange-500" />,
      color: "bg-orange-50"
    },
    {
      title: "Public Holiday",
      value: String(stats.publicHolidays || 0),
      icon: <CalendarClock className="h-6 w-6 text-green-500" />,
      color: "bg-green-50"
    },
    {
      title: "Approve/Reject",
      value: `${stats.approved || 0}/${stats.rejected || 0}`,
      icon: <CheckCircle className="h-6 w-6 text-purple-500" />,
      color: "bg-purple-50"
    }
  ];

  return (
    <div className="flex bg-white">
       <div className="w-64 border-r border-gray-200 bg-gray-50 fixed h-screen">
        <div className="p-4">
          <div className="flex flex-col gap-2">
            {filteredTabs.map((tab) => (
              <div key={tab.id}>
                <button
                  className={`text-left px-4 py-2 rounded-md !capitalize transition-colors duration-300 w-full flex items-center gap-2  cursor-pointer  ${tab.id === activeTab
                      ? "bg-primary text-white"
                      : "hover:bg-gray-100 hover:text-primary"
                    }`}
                  onClick={() => handleTabClick(tab.id)}
                >
                  {tab.icon}
                  {tab.id.replace(/([A-Z])/g, ' $1').trim()}
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
      

      <div className="flex-1 p-6 overflow-auto ml-64">
        {/* Stats Tiles */}
        {role === "companyadmin" && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {loading ? (
            Array.from({ length: 4 }).map((_, index) => (
              <div key={index} className="bg-gray-100 p-4 rounded-lg shadow-sm animate-pulse">
                <div className="h-4 bg-gray-200 rounded mb-2"></div>
                <div className="h-8 bg-gray-200 rounded"></div>
              </div>
            ))
          ) : (
            tiles.map((stat, index) => (
              <div key={index} className={`${stat.color} p-3 md:p-4 rounded-lg shadow-sm`}>
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-gray-600 text-sm">{stat.title}</p>
                    <h3 className="text-lg md:text-xl font-bold mt-1">{stat.value}</h3>
                  </div>
                  {stat.icon}
                </div>
              </div>
            ))
          )}
        </div>
        )}
        <div className="bg-white rounded-lg shadow-sm mt-4">
          {activeComponent}
        </div>
      </div>
    </div>
  );
};

export default Leave;
